/**
 * Booking and client notes with Edit and Delete for the person who wrote each one (Traci's doc, Oct 2026). The server
 * says which notes are this viewer's (`mine`) and which were changed (`edited`); the old text goes to the Audit Log.
 * Admin and Talent can also delete any booking note (`can_delete`; Traci, 21 Oct 2026).
 */
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '@/components/admin/ui/button';
import { Textarea } from '@/components/admin/ui/textarea';
import { stagingFetch } from '../api';

export type NoteRow = { id: string; note: string; created_by_name?: string | null; created_at: string; updated_at?: string | null; edited?: boolean; mine?: boolean; can_delete?: boolean; attachment?: { name: string; type?: string | null; size?: number | null } | null };

const stamp = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' }) : '—');

export default function NoteList({ notes, basePath, empty, onChanged }: { notes: NoteRow[]; basePath: string; empty: string; onChanged: () => unknown }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const save = async (n: NoteRow) => {
    setBusy(n.id);
    try { await stagingFetch(`${basePath}/${n.id}`, { method: 'PATCH', body: JSON.stringify({ note: draft }) }); setEditing(null); toast.success('Note updated'); await onChanged(); }
    catch (e: any) { toast.error(`Could not save the note: ${e.message || e}`); } finally { setBusy(null); }
  };
  const openFile = async (n: NoteRow) => {
    try { const r = await stagingFetch(`${basePath}/${n.id}/file`); window.open(r.url, '_blank', 'noopener'); }
    catch (e: any) { toast.error(`Could not open the file: ${e.message || e}`); }
  };
  const remove = async (n: NoteRow) => {
    setBusy(n.id);
    try { await stagingFetch(`${basePath}/${n.id}`, { method: 'DELETE' }); setConfirming(null); toast.success('Note deleted'); await onChanged(); }
    catch (e: any) { toast.error(`Could not delete the note: ${e.message || e}`); } finally { setBusy(null); }
  };

  if (!notes.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <>
      {notes.map((n) => (
        <div key={n.id} className="rounded-md border p-3 text-sm">
          {editing === n.id ? (
            <div className="space-y-2">
              <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} aria-label="Edit note" />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => save(n)} disabled={busy === n.id || !draft.trim()}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(null)} disabled={busy === n.id}>Cancel</Button>
              </div>
            </div>
          ) : (
            <p className="whitespace-pre-wrap">{n.note}</p>
          )}
          {n.attachment && <button type="button" className="mt-1 inline-flex items-center gap-1 text-xs text-primary underline" onClick={() => openFile(n)}>📎 {n.attachment.name}</button>}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span>
              {n.created_by_name || 'Staff'} · {stamp(n.created_at)}
              {n.edited && <span title={`Edited ${stamp(n.updated_at)}`}> · edited</span>}
            </span>
            {(n.mine || n.can_delete) && editing !== n.id && (
              confirming === n.id ? (
                <span className="flex items-center gap-2">
                  <span className="text-destructive">Delete this note?</span>
                  <button type="button" className="underline text-destructive" disabled={busy === n.id} onClick={() => remove(n)}>Delete</button>
                  <button type="button" className="underline" disabled={busy === n.id} onClick={() => setConfirming(null)}>Keep</button>
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {n.mine && <button type="button" className="underline" onClick={() => { setEditing(n.id); setDraft(n.note); setConfirming(null); }}>Edit</button>}
                  <button type="button" className="underline" onClick={() => { setConfirming(n.id); setEditing(null); }}>Delete</button>
                </span>
              )
            )}
          </div>
        </div>
      ))}
    </>
  );
}
